import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"
import { Alert, Button, Form, FormFeedback, Input, InputGroup, InputGroupText, Label } from "reactstrap"
import { useSelector } from "react-redux"
import { createSelector } from "reselect"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { useFormik } from "formik"
import * as Yup from "yup"
import withRouter from "components/Common/withRouter"
import profile from "assets/images/profile-img.png"
import logo from "../../assets/images/logo_wevalo.png"
import { get, post } from "../../helpers/api_helper"
import { getUserRoleFromToken } from "../../helpers/auth_token_helper"

const Login = () => {
  const [message, setMessage] = useState("")
  const [showError, setShowError] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  document.title = "Connexion | Wevalo"

  const handleLogin = async values => {
    setShowError(false)
    try {
      setLoading(true)
      const responseLogin = await post("auth", { ...values, auth_type: "bo" })
      if (responseLogin.status === 200) {
        localStorage.setItem("authUser", responseLogin.data.token)
        const responseStoreDetail = await get("store/mine")
        if (responseStoreDetail.status === 200) {
          localStorage.setItem("storeDetail", JSON.stringify({
            name: responseStoreDetail.data.name,
            profilePhoto: responseStoreDetail.data.profilePhoto,
          }))
        }
        const roleUser = getUserRoleFromToken()
        navigate(roleUser === "admin" || roleUser === "manager" ? "/dashboard" : "/sales")
      } else {
        setMessage(responseLogin.error.message ?? responseLogin.error["hydra:description"])
        setShowError(true)
      }
    } catch (error) {
      setShowError(true)
      setMessage(error?.response?.data?.message ?? "Email ou mot de passe incorrect.")
    } finally {
      setLoading(false)
    }
  }

  const validation = useFormik({
    initialValues: { email: "", password: "" },
    validationSchema: Yup.object({
      email: Yup.string().email("Saisissez une adresse email valide.").required("Merci de saisir votre email."),
      password: Yup.string().required("Veuillez entrer votre mot de passe."),
    }),
    onSubmit: handleLogin,
  })

  const LoginProperties = createSelector(
    state => state.Login,
    login => ({ error: login.error })
  )
  const { error } = useSelector(LoginProperties)

  useEffect(() => {
    document.body.classList.add("wevalo-login-body")
    return () => document.body.classList.remove("wevalo-login-body")
  }, [])

  return (
    <main className="wevalo-login-page">
      <section className="wevalo-login-panel">
        <div className="wevalo-login-content">
          <Link to="/" className="wevalo-login-logo">
            <img src={logo} alt="Wevalo" />
          </Link>

          <div className="wevalo-login-heading">
            <span className="wevalo-login-eyebrow">Espace partenaire</span>
            <h1>Heureux de vous revoir</h1>
            <p>Connectez-vous pour gérer vos paniers, réservations et performances.</p>
          </div>

          <div className="wevalo-login-form">
            {location.state?.accountActivated && <Alert color="success">Votre compte a bien été activé. Vous pouvez maintenant vous connecter.</Alert>}
            {showError && message && <div className="alert alert-danger">{message}</div>}
            {error && <Alert color="danger">{error}</Alert>}

            <Form onSubmit={event => {
              event.preventDefault()
              validation.handleSubmit()
            }}>
              <div className="wevalo-login-field">
                <Label>Email professionnel</Label>
                <Input
                  name="email"
                  className="wevalo-login-input"
                  placeholder="vous@commerce.com"
                  type="email"
                  autoComplete="email"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.email}
                  invalid={Boolean(validation.touched.email && validation.errors.email)}
                />
                {validation.touched.email && validation.errors.email && <FormFeedback>{validation.errors.email}</FormFeedback>}
              </div>

              <div className="wevalo-login-field">
                <div className="wevalo-login-label-row">
                  <Label>Mot de passe</Label>
                  <Link to="/forgot-password">Mot de passe oublié ?</Link>
                </div>
                <InputGroup className="wevalo-login-password">
                  <Input
                    name="password"
                    className="wevalo-login-input"
                    value={validation.values.password}
                    type={showPassword ? "text" : "password"}
                    placeholder="Votre mot de passe"
                    autoComplete="current-password"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    invalid={Boolean(validation.touched.password && validation.errors.password)}
                  />
                  <InputGroupText onClick={() => setShowPassword(!showPassword)}>
                    <i className={`bx ${showPassword ? "bx-hide" : "bx-show"} font-size-18`} />
                  </InputGroupText>
                </InputGroup>
                {validation.touched.password && validation.errors.password && <div className="invalid-feedback d-block">{validation.errors.password}</div>}
              </div>

              <div className="d-grid">
                <Button
                  color="primary"
                  className="wevalo-login-submit"
                  type="submit"
                  disabled={loading || !validation.values.email.trim() || !validation.values.password}
                >
                  {loading ? <><span className="spinner-border spinner-border-sm" /> Connexion...</> : <>Se connecter <i className="bx bx-right-arrow-alt" /></>}
                </Button>
              </div>

              <div className="wevalo-login-divider"><span>Vous débutez avec Wevalo ?</span></div>

              <div className="d-grid">
                <Button tag={Link} to="/partner/signup" className="wevalo-login-partner">
                  <i className="bx bx-store" /> Créer un espace partenaire
                </Button>
              </div>
            </Form>
          </div>

          <p className="wevalo-login-security"><i className="bx bx-lock-alt" /> Connexion sécurisée à votre espace professionnel</p>
        </div>
      </section>

      <aside className="wevalo-login-visual">
        <div className="wevalo-login-visual-content">
          <span className="wevalo-login-visual-badge"><i className="bx bx-leaf" /> Anti-gaspillage, simplement</span>
          <h2>Transformez vos invendus en opportunités.</h2>
          <p>Publiez vos paniers, développez votre visibilité et suivez votre impact depuis un seul espace.</p>
          <div className="wevalo-login-benefits">
            <span><i className="bx bx-check" /> Gestion simple et autonome</span>
            <span><i className="bx bx-check" /> Réservations en temps réel</span>
            <span><i className="bx bx-check" /> Impact positif mesurable</span>
          </div>
          <img src={profile} alt="" aria-hidden="true" />
        </div>
      </aside>
    </main>
  )
}

export default withRouter(Login)

Login.propTypes = {
  history: PropTypes.object,
}
